import { Card, CardContent } from './ui/card';

const PersonaStats = ({ user }) => {
  if (!user) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground text-sm">No information available yet</p>
      </div>
    );
  }

  const hasAbout = user.aboutMe || user.sports?.length || user.persona || user.location || user.organization || user.position || user.speciality || user.phone || user.contactEmail || user.website;
  const hasStats = user.stats && Object.keys(user.stats).length > 0;

  if (!hasAbout && !hasStats) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground text-sm">No information available yet</p>
      </div>
    );
  }

  const safeArray = (val) => {
    if (!val) return [];
    if (typeof val === 'string') return [val];
    if (!Array.isArray(val)) return [String(val)];
    return val;
  };

  const formatValue = (item) => {
    if (typeof item === 'string') return item;
    if (typeof item === 'object' && item !== null) {
      const parts = [item.title || item.name || item.event || ''];
      if (item.time) parts.push(item.time);
      if (item.date) parts.push(item.date);
      if (item.year) parts.push(`(${item.year})`);
      return parts.filter(Boolean).join(' ');
    }
    return String(item);
  };

  // Single row: label on left, value on right
  const Row = ({ label, value }) => (
    <div className="flex justify-between items-center py-2 border-b border-border last:border-0">
      <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{value}</span>
    </div>
  );

  // Section heading
  const Section = ({ title, children }) => (
    <div className="mb-5 pb-1">
      <h3 className="text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">{title}</h3>
      <div>{children}</div>
    </div>
  );

  // List items displayed as rows (same style as Club Info rows)
  const ListRows = ({ items }) => (
    <div>
      {items.map((item, i) => (
        <div key={i} className="flex justify-between items-center py-2 border-b border-border last:border-0">
          <span className="text-sm text-foreground">{formatValue(item)}</span>
        </div>
      ))}
    </div>
  );

  const renderContent = () => {
    const stats = user.stats || {};
    const persona = user.persona;

    return (
      <>
        {/* --- ABOUT SECTION (all personas) --- */}
        {user.aboutMe && (
          <Section title="About Me">
            <p className="text-sm text-foreground whitespace-pre-line leading-relaxed">{user.aboutMe}</p>
          </Section>
        )}

        {(user.sports?.length > 0 || user.persona || user.location || user.organization || user.position || user.speciality) && (
          <Section title="Details">
            {user.sports?.length > 0 && <Row label="Sport" value={user.sports.join(', ')} />}
            {user.persona && <Row label="Role" value={user.persona.charAt(0).toUpperCase() + user.persona.slice(1)} />}
            {user.location && <Row label="Location" value={user.location} />}
            {user.organization && <Row label="Organization" value={user.organization} />}
            {user.position && <Row label="Position" value={user.position} />}
            {user.speciality && <Row label="Speciality" value={user.speciality} />}
          </Section>
        )}

        {(user.phone || user.contactEmail || user.website) && (
          <Section title="Contact">
            {user.phone && <Row label="Phone" value={user.phone} />}
            {user.contactEmail && <Row label="Email" value={user.contactEmail} />}
            {user.website && (
              <div className="flex justify-between items-center py-2 border-b border-border last:border-0">
                <span className="text-xs text-gray-500 dark:text-gray-400">Website</span>
                <a
                  href={user.website.startsWith('http') ? user.website : `https://${user.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-semibold text-primary hover:underline"
                >
                  {user.website}
                </a>
              </div>
            )}
          </Section>
        )}

        {/* --- ATHLETE --- */}
        {persona === 'athlete' && (
          <>
            {(stats.competitions || stats.wins) && (
              <Section title="Performance">
                {stats.competitions != null && <Row label="Competitions" value={stats.competitions} />}
                {stats.wins != null && <Row label="Wins" value={stats.wins} />}
                {stats.competitions > 0 && (
                  <Row label="Win Rate" value={`${Math.round((stats.wins / stats.competitions) * 100)}%`} />
                )}
              </Section>
            )}

            {(user.height || user.weight || user.age || user.position) && (
              <Section title="Physical Stats">
                {user.height && <Row label="Height" value={user.height} />}
                {user.weight && <Row label="Weight" value={user.weight} />}
                {user.age && <Row label="Age" value={`${user.age} yrs`} />}
                {user.position && <Row label="Position" value={user.position} />}
                {user.speciality && <Row label="Speciality" value={user.speciality} />}
                {user.dateOfBirth && <Row label="Born" value={new Date(user.dateOfBirth).getFullYear()} />}
              </Section>
            )}

            {safeArray(stats.personalBests).length > 0 && (
              <Section title="Personal Bests">
                <ListRows items={safeArray(stats.personalBests)} />
              </Section>
            )}
          </>
        )}

        {/* --- COACH --- */}
        {persona === 'coach' && (
          <>
            {(stats.yearsExperience || stats.athletesTrained || stats.championships) && (
              <Section title="Experience">
                {stats.yearsExperience && <Row label="Years Experience" value={stats.yearsExperience} />}
                {stats.athletesTrained && <Row label="Athletes Trained" value={stats.athletesTrained} />}
                {stats.championships && <Row label="Championships" value={stats.championships} />}
              </Section>
            )}

            {stats.philosophy && (
              <Section title="Coaching Philosophy">
                <p className="text-sm text-foreground leading-relaxed">{stats.philosophy}</p>
              </Section>
            )}

            {safeArray(stats.certifications).length > 0 && (
              <Section title="Certifications">
                <ListRows items={safeArray(stats.certifications)} />
              </Section>
            )}
          </>
        )}

        {/* --- CLUB --- */}
        {persona === 'club' && (
          <>
            {(stats.founded || stats.members || stats.trophies) && (
              <Section title="Club Info">
                {stats.founded && <Row label="Founded" value={stats.founded} />}
                {stats.members && <Row label="Members" value={stats.members} />}
                {stats.trophies && <Row label="Trophies" value={stats.trophies} />}
              </Section>
            )}

            {safeArray(stats.facilities).length > 0 && (
              <Section title="Facilities">
                {safeArray(stats.facilities).map((item, i) => (
                  <div key={i} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                    <span className="text-sm text-foreground">{formatValue(item)}</span>
                  </div>
                ))}
              </Section>
            )}

            {safeArray(stats.achievements).length > 0 && (
              <Section title="Achievements">
                {safeArray(stats.achievements).map((item, i) => (
                  <div key={i} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                    <span className="text-sm text-foreground">{formatValue(item)}</span>
                  </div>
                ))}
              </Section>
            )}
          </>
        )}

        {/* --- NUTRITIONIST --- */}
        {persona === 'nutritionist' && (
          <>
            {(stats.yearsExperience || stats.clientsHelped) && (
              <Section title="Experience">
                {stats.yearsExperience && <Row label="Years Experience" value={stats.yearsExperience} />}
                {stats.clientsHelped && <Row label="Clients Helped" value={stats.clientsHelped} />}
              </Section>
            )}

            {safeArray(stats.specializations).length > 0 && (
              <Section title="Specializations">
                <ListRows items={safeArray(stats.specializations)} />
              </Section>
            )}

            {safeArray(stats.certifications).length > 0 && (
              <Section title="Certifications">
                <ListRows items={safeArray(stats.certifications)} />
              </Section>
            )}

            {safeArray(stats.qualifications).length > 0 && (
              <Section title="Qualifications">
                <ListRows items={safeArray(stats.qualifications)} />
              </Section>
            )}
          </>
        )}

        {/* --- PHYSIO --- */}
        {persona === 'physio' && (
          <>
            {(stats.yearsExperience || stats.athletesTreated) && (
              <Section title="Experience">
                {stats.yearsExperience && <Row label="Years Experience" value={stats.yearsExperience} />}
                {stats.athletesTreated && <Row label="Athletes Treated" value={stats.athletesTreated} />}
              </Section>
            )}

            {safeArray(stats.specializations).length > 0 && (
              <Section title="Specializations">
                <ListRows items={safeArray(stats.specializations)} />
              </Section>
            )}

            {safeArray(stats.certifications).length > 0 && (
              <Section title="Certifications">
                <ListRows items={safeArray(stats.certifications)} />
              </Section>
            )}

            {safeArray(stats.qualifications).length > 0 && (
              <Section title="Qualifications">
                <ListRows items={safeArray(stats.qualifications)} />
              </Section>
            )}
          </>
        )}

        {/* --- FAN --- */}
        {persona === 'fan' && (
          <>
            <Section title="Fan Details">
              {stats.fanSince && <Row label="Fan Since" value={stats.fanSince} />}
              {stats.matchesAttended && <Row label="Matches Attended" value={stats.matchesAttended} />}
              {stats.eventsAttended && <Row label="Events Attended" value={stats.eventsAttended} />}
            </Section>

            {safeArray(stats.favoriteTeams).length > 0 && (
              <Section title="Favorite Teams">
                <ListRows items={safeArray(stats.favoriteTeams)} />
              </Section>
            )}

            {safeArray(stats.favoritePlayers).length > 0 && (
              <Section title="Favorite Players">
                <ListRows items={safeArray(stats.favoritePlayers)} />
              </Section>
            )}

            {safeArray(stats.interests).length > 0 && (
              <Section title="Interests">
                <ListRows items={safeArray(stats.interests)} />
              </Section>
            )}
          </>
        )}
      </>
    );
  };

  return (
    <Card className="border-0 shadow-sm" data-testid="persona-stats">
      <CardContent className="p-4">
        {renderContent()}
      </CardContent>
    </Card>
  );
};

export default PersonaStats;
